import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { CircularProgress } from '@/components/ui/circular-progress';
import foxDetective from '@/assets/fox-detective.webp';
import { motion } from 'framer-motion';

export const OnboardingProgressCard = () => {
  const navigate = useNavigate();
  const { steps, completionPercentage, isFullyComplete, isLoading } = useOnboardingProgress();

  if (isLoading) {
    return null;
  }

  if (isFullyComplete) {
    return null;
  }

  const nextIncompleteStep = steps.find(step => !step.completed);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        {/* Fox Detective Image - Left Side */}
        <div className="hidden lg:flex items-center justify-center p-6 border-r border-border/20">
          <motion.img
            src={foxDetective}
            alt="Fox mascot"
            className="w-40 h-40 object-contain"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Content - Right Side */}
        <div className="flex-1">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-xl mb-2">Dokończ konfigurację</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Dokończ konfigurację, aby w pełni wykorzystać możliwości konta
                </p>
              </div>
              
              {/* Circular Progress */}
              <div className="flex-shrink-0">
                <CircularProgress 
                  value={completionPercentage} 
                  size={80} 
                  strokeWidth={8}
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              {steps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {step.completed ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      </motion.div>
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <span className={step.completed ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}>
                      {step.label}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {nextIncompleteStep?.path && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Button
                  onClick={() => navigate(nextIncompleteStep.path!)}
                  className="w-full"
                  size="lg"
                >
                  Kontynuuj konfigurację
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}
          </CardContent>
        </div>
      </div>
    </Card>
  );
};
